import { useId, useState } from 'react';
import { VisuallyHidden } from '../atoms/VisuallyHidden';
import styles from './BarChart.module.css';

export interface BarChartSeries {
  key: string;
  label: string;
  /** One value per category, in category order. */
  values: number[];
}

export interface BarChartProps {
  /** Accessible name; also the caption of the table view. */
  label: string;
  /** Labels along the x-axis, e.g. months. */
  categories: string[];
  /** Long labels for tooltip and table, e.g. "Januar 2026" (defaults to `categories`). */
  categoryTitles?: string[];
  /** At most two series (colour slots 1 and 2). */
  series: BarChartSeries[];
  format: (value: number) => string;
  /** Header of the first table column. */
  categoryHeader: string;
}

const SLOTS = ['var(--chart-1)', 'var(--chart-2)'];

/** A rounded upper bound for the axis, e.g. 1234 → 1500. */
function niceMax(max: number): number {
  if (max <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(max));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  return (steps.find((s) => s * exp >= max) ?? 10) * exp;
}

/**
 * Grouped bars per category (e.g. income and expenses per month). Hover or
 * focus a column for its values; screen readers get a table.
 */
export function BarChart({
  label,
  categories,
  categoryTitles,
  series,
  format,
  categoryHeader,
}: BarChartProps) {
  const id = useId();
  const [active, setActive] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const titles = categoryTitles ?? categories;
  const ticks = [max, max / 2, 0];

  return (
    <figure className={styles.figure} aria-labelledby={`${id}-label`}>
      <VisuallyHidden>
        <span id={`${id}-label`}>{label}</span>
      </VisuallyHidden>
      {series.length > 1 && (
        <ul className={styles.legend} aria-hidden="true">
          {series.map((s, i) => (
            <li key={s.key}>
              <span className={styles.swatch} style={{ background: SLOTS[i] }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.plot} aria-hidden="true">
        <div className={styles.axis}>
          {ticks.map((t) => (
            <span key={t}>{format(t)}</span>
          ))}
        </div>
        <div className={styles.area}>
          <div className={styles.gridLayer}>
            {ticks.map((t) => (
              <span key={t} className={styles.grid} style={{ bottom: `${(t / max) * 100}%` }} />
            ))}
          </div>
          <div className={styles.columns}>
            {categories.map((c, ci) => (
              <div
                key={c}
                className={styles.column}
                data-active={active === ci || undefined}
                onMouseEnter={() => setActive(ci)}
                onMouseLeave={() => setActive(null)}
              >
                <div className={styles.bars}>
                  {series.map((s, si) => (
                    <span
                      key={s.key}
                      className={styles.bar}
                      style={{
                        height: `${(Math.max(0, s.values[ci] ?? 0) / max) * 100}%`,
                        background: SLOTS[si],
                      }}
                    />
                  ))}
                </div>
                <span className={styles.tick}>{c}</span>
                {active === ci && (
                  <div className={styles.tooltip} role="presentation">
                    <strong>{titles[ci]}</strong>
                    {series.map((s, si) => (
                      <span key={s.key} className={styles.tipRow}>
                        <span className={styles.swatch} style={{ background: SLOTS[si] }} />
                        {s.label}
                        <span className={styles.tipValue}>{format(s.values[ci] ?? 0)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <VisuallyHidden>
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">{categoryHeader}</th>
              {series.map((s) => (
                <th key={s.key} scope="col">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {titles.map((c, ci) => (
              <tr key={c}>
                <th scope="row">{c}</th>
                {series.map((s) => (
                  <td key={s.key}>{format(s.values[ci] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </VisuallyHidden>
    </figure>
  );
}
